import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GoogleLoginButton from "./GoogleLoginButton";
import TurnstileWidget from "./TurnstileWidget";
import { registrationSiteKey } from "../lib/turnstile";

export default function CourseLoginModal({ open, onClose }) {
  const { t } = useTranslation();
  const { login } = useAuth();
  const emailRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [resetChallenge, setResetChallenge] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const siteKey = registrationSiteKey(window.location.hostname, process.env.REACT_APP_TURNSTILE_SITE_KEY);

  useEffect(() => {
    if (!open) return undefined;
    emailRef.current?.focus();
    const onKeyDown = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!captchaToken || submitting) return;
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    try {
      await login(String(form.get("email") || ""), String(form.get("password") || ""), false, captchaToken);
      onClose(false);
    } catch {
      toast.error(t("courseGuest.loginFailed"));
    } finally {
      setSubmitting(false);
      setCaptchaToken("");
      setResetChallenge((value) => value + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section role="dialog" aria-modal="true" aria-labelledby="course-login-title"
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="course-login-title" className="text-lg font-bold text-slate-900">{t("courseGuest.loginTitle")}</h2>
            <p className="mt-1 text-sm text-slate-600">{t("courseGuest.loginDescription")}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t("courseGuest.cancel")}
            className="rounded-lg px-2 text-xl text-slate-700 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-indigo-600">×</button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <label className="block text-sm font-medium text-slate-700" htmlFor="course-login-email">{t("courseGuest.email")}</label>
          <input ref={emailRef} id="course-login-email" name="email" type="email" autoComplete="email" required
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600" />
          <label className="block text-sm font-medium text-slate-700" htmlFor="course-login-password">{t("courseGuest.password")}</label>
          <input id="course-login-password" name="password" type="password" autoComplete="current-password" required
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600" />
          {siteKey ? <TurnstileWidget siteKey={siteKey} action="login" onTokenChange={setCaptchaToken}
            resetSignal={resetChallenge} /> : <p role="alert" className="text-sm text-red-700">{t("courseGuest.securityUnavailable")}</p>}
          <button type="submit" disabled={!captchaToken || submitting}
            className="w-full rounded-lg bg-indigo-800 px-4 py-2.5 font-semibold text-white hover:bg-indigo-900 focus-visible:ring-2 focus-visible:ring-indigo-600 disabled:opacity-50">
            {t("courseGuest.signIn")}
          </button>
        </form>
        <div className="my-4 text-center text-xs text-slate-500">{t("courseGuest.or")}</div>
        <GoogleLoginButton label={t("courseGuest.googleSignIn")} />
        <div className="mt-4 flex justify-between text-sm">
          <Link to="/forgot-pwd" onClick={onClose} className="text-indigo-800 underline">{t("forgot_password")}</Link>
          <Link to="/sign-up" onClick={onClose} className="text-indigo-800 underline">{t("dont_have_an_account")}</Link>
        </div>
      </section>
    </div>
  );
}
